import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { BACKEND_URL } from '../config';

// Composant de restriction d'accès réutilisable
const AccessRestrictionPage = ({ moduleCode, moduleName, moduleIcon, reason, userSubscription, onBack }) => {
  const [availablePlans, setAvailablePlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(true);

  const loadAvailablePlans = useCallback(async () => {
    try {
      const plansRes = await api.get('/subscriptions/plans');
      const plansWithModule = plansRes.data
        .filter(plan => plan.modules && plan.modules.includes(moduleCode))
        .sort((a, b) => (a.display_order || 0) - (b.display_order || 0) || (a.prix_mensuel - b.prix_mensuel));
      setAvailablePlans(plansWithModule);
    } catch (err) {
      console.error('Erreur chargement packs:', err);
    } finally {
      setLoadingPlans(false);
    }
  }, [moduleCode]);

  useEffect(() => {
    loadAvailablePlans();
  }, [loadAvailablePlans]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full">
        <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-orange-500 to-red-500 p-8 text-center text-white relative overflow-hidden">
            <div className="absolute inset-0 bg-black/10"></div>
            <div className="relative">
              <span className="text-7xl mb-4 block animate-bounce">🔐</span>
              <h1 className="text-3xl md:text-4xl font-bold mb-3">Accès Restreint</h1>
              <p className="text-orange-100 text-lg">
                Cette fonctionnalité nécessite un abonnement adapté
              </p>
            </div>
          </div>
          
          {/* Contenu */}
          <div className="p-6 md:p-8">
            {/* Module requis */}
            <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-2xl p-5 mb-6 border-2 border-purple-100">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center shadow-md">
                  <span className="text-4xl">{moduleIcon || '📦'}</span>
                </div>
                <div className="flex-1">
                  <div className="text-xs text-purple-600 font-semibold uppercase tracking-wide mb-1">Module requis</div>
                  <div className="text-xl font-bold text-gray-900">{moduleName}</div>
                </div>
              </div>
            </div>
            
            {/* Message personnalisé */}
            <div className="bg-blue-50 border-2 border-blue-200 rounded-2xl p-5 mb-6">
              <div className="flex items-start gap-3">
                <span className="text-2xl">💡</span>
                <div>
                  <div className="font-semibold text-blue-800 mb-1">
                    {userSubscription ? "Upgrade nécessaire" : "Aucun abonnement actif"}
                  </div>
                  <p className="text-blue-700 text-sm">{reason}</p>
                </div>
              </div>
            </div>
            
            {/* Packs disponibles */}
            {loadingPlans ? (
              <div className="flex justify-center py-4">
                <div className="animate-spin rounded-full h-8 w-8 border-4 border-purple-500 border-t-transparent"></div>
              </div>
            ) : availablePlans.length > 0 ? (
              <>
                <div className="text-center mb-4">
                  <span className="text-sm font-semibold text-gray-600 bg-gray-100 px-4 py-2 rounded-full">
                    ✨ {availablePlans.length === 1 
                      ? `1 pack donne accès à "${moduleName}"` 
                      : `${availablePlans.length} packs donnent accès à "${moduleName}"`}
                  </span>
                </div>
                
                <div className="grid gap-3">
                  {availablePlans.map((plan, index) => (
                    <div 
                      key={plan.id}
                      className={`border-2 rounded-2xl p-4 flex items-center justify-between hover:shadow-lg transition-all cursor-pointer ${
                        index === 0 
                          ? 'border-purple-300 bg-purple-50/50 ring-2 ring-purple-200' 
                          : 'border-gray-100 hover:border-purple-300'
                      }`}
                      onClick={() => window.location.href = '/my-subscription'}
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: plan.couleur + '20' }}>
                          <span className="text-2xl">{plan.icone}</span>
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-bold text-gray-900">{plan.nom}</span>
                            {index === 0 && (
                              <span className="text-xs px-2 py-0.5 bg-purple-600 text-white rounded-full">Recommandé</span>
                            )}
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            {plan.module_names?.length || 0} module{(plan.module_names?.length || 0) > 1 ? 's' : ''} inclus
                          </div>
                          <div className="text-xs text-gray-400 mt-0.5">
                            {plan.module_names?.slice(0, 4).join(' • ')}
                            {plan.module_names?.length > 4 && ` +${plan.module_names.length - 4}`}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-lg font-bold" style={{ color: plan.couleur }}>
                          {Number(plan.prix_mensuel).toLocaleString()}
                        </div>
                        <div className="text-xs text-gray-400">XOF/mois</div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="bg-gray-50 border-2 border-gray-200 rounded-2xl p-5 mb-6 text-center">
                <span className="text-4xl mb-3 block">🔒</span>
                <div className="font-semibold text-gray-700">
                  Le module "{moduleName}" n'est disponible dans aucun pack actuellement
                </div>
                <p className="text-sm text-gray-500 mt-2">
                  Contactez l'administrateur pour plus d'informations.
                </p>
              </div>
            )}
            
            {/* Bouton principal */}
            <div className="mt-8">
              <Link
                to="/my-subscription"
                className="w-full flex items-center justify-center gap-3 px-8 py-4 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-2xl font-bold text-lg hover:from-purple-700 hover:to-indigo-700 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
              >
                <span>🚀</span>
                <span>Voir les packs d'abonnement</span>
              </Link>
            </div>
            
            {/* Bouton retour */}
            {onBack && (
              <div className="mt-4 text-center">
                <button
                  onClick={onBack}
                  className="text-gray-400 hover:text-gray-600 transition-colors text-sm"
                >
                  ← Retourner à la page précédente
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Définitions des options de personnalisation
const THEMES = [
  { id: 'default', name: 'Par défaut', colors: { primary: '#4F46E5', secondary: '#7C3AED', accent: '#10B981' } },
  { id: 'sunset', name: 'Coucher de soleil', colors: { primary: '#F97316', secondary: '#EF4444', accent: '#FBBF24' } },
  { id: 'ocean', name: 'Océan', colors: { primary: '#0EA5E9', secondary: '#06B6D4', accent: '#14B8A6' } },
  { id: 'forest', name: 'Forêt', colors: { primary: '#22C55E', secondary: '#16A34A', accent: '#84CC16' } },
  { id: 'berry', name: 'Berry', colors: { primary: '#EC4899', secondary: '#D946EF', accent: '#F472B6' } },
  { id: 'dark', name: 'Sombre', colors: { primary: '#6366F1', secondary: '#8B5CF6', accent: '#A78BFA' } },
  { id: 'earth', name: 'Terre', colors: { primary: '#A16207', secondary: '#CA8A04', accent: '#D97706' } },
  { id: 'custom', name: 'Personnalisé', colors: null }
];

const FONTS = [
  { id: 'Inter', name: 'Inter (Moderne)' },
  { id: 'Poppins', name: 'Poppins (Élégant)' },
  { id: 'Roboto', name: 'Roboto (Classique)' },
  { id: 'Montserrat', name: 'Montserrat (Bold)' },
  { id: 'Open Sans', name: 'Open Sans (Lisible)' },
  { id: 'Playfair Display', name: 'Playfair (Luxueux)' },
  { id: 'Nunito', name: 'Nunito (Doux)' }
];

const CARD_STYLES = [
  { id: 'rounded', name: 'Arrondi', preview: 'rounded-xl' },
  { id: 'square', name: 'Carré', preview: 'rounded-none' },
  { id: 'soft', name: 'Doux', preview: 'rounded-2xl' },
  { id: 'pill', name: 'Pilule', preview: 'rounded-3xl' }
];

const BUTTON_STYLES = [
  { id: 'rounded', name: 'Arrondi', preview: 'rounded-lg' },
  { id: 'square', name: 'Carré', preview: 'rounded-none' },
  { id: 'pill', name: 'Pilule', preview: 'rounded-full' }
];

const HEADER_STYLES = [
  { id: 'classic', name: 'Classique', desc: 'Bannière avec logo superposé' },
  { id: 'minimal', name: 'Minimal', desc: 'Logo et titre uniquement' },
  { id: 'centered', name: 'Centré', desc: 'Logo centré avec bannière' },
  { id: 'hero', name: 'Héro', desc: 'Grande bannière plein écran' }
];

// Composant Toast moderne
const Toast = ({ message, type, onClose }) => {
  const bgColors = {
    success: 'bg-green-500',
    error: 'bg-red-500',
    warning: 'bg-amber-500',
    info: 'bg-blue-500'
  };
  const icons = {
    success: '✓',
    error: '✕',
    warning: '⚠',
    info: 'ℹ'
  };

  useEffect(() => {
    const timer = setTimeout(onClose, 4000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className={`fixed top-4 right-4 z-50 flex items-center gap-3 px-4 py-3 rounded-lg shadow-lg text-white ${bgColors[type]} animate-slide-in`}>
      <span className="text-lg font-bold">{icons[type]}</span>
      <span className="font-medium">{message}</span>
      <button onClick={onClose} className="ml-2 hover:opacity-80">✕</button>
    </div>
  );
};

const BusinessShop = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [shop, setShop] = useState(null);
  const [stats, setStats] = useState(null);
  const [activeTab, setActiveTab] = useState('general');
  const [toast, setToast] = useState(null);
  const [hasAccess, setHasAccess] = useState(true);
  const [accessInfo, setAccessInfo] = useState(null);
  const [activeCssCategory, setActiveCssCategory] = useState(0);
  
  // États pour le filtre de période du CA
  const [revenuePeriod, setRevenuePeriod] = useState('all');
  const [customDateStart, setCustomDateStart] = useState('');
  const [customDateEnd, setCustomDateEnd] = useState('');
  const [revenueAmount, setRevenueAmount] = useState(0);
  const [loadingRevenue, setLoadingRevenue] = useState(false);

  // Fonction pour afficher un toast
  const showToast = (message, type = 'info') => {
    setToast({ message, type });
  };
  const [formData, setFormData] = useState({
    slug: '',
    nom: '',
    description: '',
    logo: '',
    banner: '',
    phone: '',
    whatsapp: '',
    address: '',
    is_active: false,
    show_stock: true,
    delivery_enabled: true,
    pickup_enabled: true,
    delivery_fee: 0,
    min_order_amount: 0,
    currency: 'FCFA',
    // Options de personnalisation
    theme: 'default',
    primary_color: '#4F46E5',
    secondary_color: '#7C3AED',
    accent_color: '#10B981',
    text_color: '#1F2937',
    background_color: '#F9FAFB',
    card_style: 'rounded',
    button_style: 'rounded',
    font_family: 'Inter',
    header_style: 'classic',
    product_grid_cols: 4,
    show_category_filter: true,
    show_search: true,
    show_banner: true,
    show_description: true,
    show_contact: true,
    announcement_text: '',
    announcement_bg_color: '#FEF3C7',
    social_facebook: '',
    social_instagram: '',
    social_tiktok: '',
    custom_css: '',
    // Nouveaux champs disposition
    floating_whatsapp: false,
    floating_cart: true,
    scroll_to_top: true,
    sticky_categories: true,
    modal_animation: 'scale',
    floating_position: 'bottom-right'
  });
  const [slugAvailable, setSlugAvailable] = useState(null);
  const [slugChecking, setSlugChecking] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const logoInputRef = useRef(null);
  const bannerInputRef = useRef(null);

  const checkAccessAndLoadShop = useCallback(async () => {
    try {
      // Vérifier l'accès au module shop
      const accessRes = await api.get('/business/access');
      setAccessInfo(accessRes.data);
      
      if (!accessRes.data.has_shop_module) {
        setHasAccess(false);
        setLoading(false);
        return;
      }
      
      // Si accès OK, charger la boutique
      await loadShop();
    } catch (err) {
      console.error(err);
      setHasAccess(false);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAccessAndLoadShop();
  }, [checkAccessAndLoadShop]);

  const loadShop = async () => {
    try {
      const res = await api.get('/shop');
      if (res.data.exists) {
        setShop(res.data.shop);
        setStats(res.data.stats);
        setFormData({
          slug: res.data.shop.slug || '',
          nom: res.data.shop.nom || '',
          description: res.data.shop.description || '',
          logo: res.data.shop.logo || '',
          banner: res.data.shop.banner || '',
          phone: res.data.shop.phone || '',
          whatsapp: res.data.shop.whatsapp || '',
          address: res.data.shop.address || '',
          is_active: res.data.shop.is_active === 1,
          show_stock: res.data.shop.show_stock === 1,
          delivery_enabled: res.data.shop.delivery_enabled === 1,
          pickup_enabled: res.data.shop.pickup_enabled === 1,
          delivery_fee: res.data.shop.delivery_fee || 0,
          min_order_amount: res.data.shop.min_order_amount || 0,
          currency: res.data.shop.currency || 'FCFA',
          // Options de personnalisation
          theme: res.data.shop.theme || 'default',
          primary_color: res.data.shop.primary_color || '#4F46E5',
          secondary_color: res.data.shop.secondary_color || '#7C3AED',
          accent_color: res.data.shop.accent_color || '#10B981',
          text_color: res.data.shop.text_color || '#1F2937',
          background_color: res.data.shop.background_color || '#F9FAFB',
          card_style: res.data.shop.card_style || 'rounded',
          button_style: res.data.shop.button_style || 'rounded',
          font_family: res.data.shop.font_family || 'Inter',
          header_style: res.data.shop.header_style || 'classic',
          product_grid_cols: parseInt(res.data.shop.product_grid_cols) || 4,
          show_category_filter: res.data.shop.show_category_filter === 1,
          show_search: res.data.shop.show_search === 1,
          show_banner: res.data.shop.show_banner === 1,
          show_description: res.data.shop.show_description === 1,
          show_contact: res.data.shop.show_contact === 1,
          announcement_text: res.data.shop.announcement_text || '',
          announcement_bg_color: res.data.shop.announcement_bg_color || '#FEF3C7',
          social_facebook: res.data.shop.social_facebook || '',
          social_instagram: res.data.shop.social_instagram || '',
          social_tiktok: res.data.shop.social_tiktok || '',
          custom_css: res.data.shop.custom_css || '',
          // Disposition et éléments flottants
          floating_whatsapp: res.data.shop.floating_whatsapp === 1,
          floating_cart: res.data.shop.floating_cart === 1,
          scroll_to_top: res.data.shop.scroll_to_top === 1,
          sticky_categories: res.data.shop.sticky_categories === 1,
          modal_animation: res.data.shop.modal_animation || 'scale',
          floating_position: res.data.shop.floating_position || 'bottom-right'
        });
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Charger le CA selon la période sélectionnée
  const loadRevenueByPeriod = async (period, startDate = null, endDate = null) => {
    if (!shop) return;
    
    setLoadingRevenue(true);
    try {
      let params = new URLSearchParams();
      params.append('period', period);
      
      if (period === 'custom' && startDate && endDate) {
        params.append('start_date', startDate);
        params.append('end_date', endDate);
      }
      
      const res = await api.get(`/shop/revenue?${params.toString()}`);
      if (res.data.success) {
        setRevenueAmount(res.data.revenue || 0);
      }
    } catch (err) {
      console.error('Erreur chargement CA:', err);
    } finally {
      setLoadingRevenue(false);
    }
  };

  // Effet pour charger le CA quand la période change
  useEffect(() => {
    if (shop && revenuePeriod !== 'custom') {
      loadRevenueByPeriod(revenuePeriod);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shop, revenuePeriod]);

  // Effet pour initialiser le CA total au chargement
  useEffect(() => {
    if (stats) {
      setRevenueAmount(stats.total_revenue || 0);
    }
  }, [stats]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Vérifier le slug en temps réel
    if (name === 'slug') {
      checkSlug(value);
    }
  };

  // Appliquer un thème prédéfini
  const applyTheme = (themeId) => {
    const theme = THEMES.find(t => t.id === themeId);
    if (theme && theme.colors) {
      setFormData(prev => ({
        ...prev,
        theme: themeId,
        primary_color: theme.colors.primary,
        secondary_color: theme.colors.secondary,
        accent_color: theme.colors.accent
      }));
    } else {
      setFormData(prev => ({ ...prev, theme: themeId }));
    }
  };

  const checkSlug = async (slug) => {
    if (!slug || slug.length < 3) {
      setSlugAvailable(null);
      return;
    }

    setSlugChecking(true);
    try {
      const res = await api.get(`/shop/check-slug/${slug.toLowerCase().replace(/[^a-z0-9-]/g, '-')}`);
      setSlugAvailable(res.data.available);
    } catch (err) {
      setSlugAvailable(null);
    } finally {
      setSlugChecking(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.slug || formData.slug.length < 3) {
      showToast('Le slug doit avoir au moins 3 caractères', 'warning');
      return;
    }

    if (!formData.nom) {
      showToast('Le nom de la boutique est requis', 'warning');
      return;
    }

    // Debug: voir ce qui est envoyé
    console.log('=== DEBUG SUBMIT ===');
    console.log('formData complet:', JSON.stringify(formData, null, 2));
    console.log('====================');

    setSaving(true);
    try {
      const res = await api.post('/shop', formData);
      console.log('Réponse API:', res.data);
      if (res.data.success) {
        setShop(res.data.shop);
        // Mettre à jour formData avec les données retournées par le serveur
        setFormData(prev => ({
          ...prev,
          theme: res.data.shop.theme || 'default',
          primary_color: res.data.shop.primary_color || '#4F46E5',
          secondary_color: res.data.shop.secondary_color || '#7C3AED',
          accent_color: res.data.shop.accent_color || '#10B981',
          text_color: res.data.shop.text_color || '#1F2937',
          background_color: res.data.shop.background_color || '#F9FAFB',
          card_style: res.data.shop.card_style || 'rounded',
          button_style: res.data.shop.button_style || 'rounded',
          font_family: res.data.shop.font_family || 'Inter',
          header_style: res.data.shop.header_style || 'classic',
          product_grid_cols: parseInt(res.data.shop.product_grid_cols) || 4,
          show_category_filter: res.data.shop.show_category_filter === 1,
          show_search: res.data.shop.show_search === 1,
          show_banner: res.data.shop.show_banner === 1,
          show_description: res.data.shop.show_description === 1,
          show_contact: res.data.shop.show_contact === 1,
          announcement_text: res.data.shop.announcement_text || '',
          announcement_bg_color: res.data.shop.announcement_bg_color || '#FEF3C7',
          social_facebook: res.data.shop.social_facebook || '',
          social_instagram: res.data.shop.social_instagram || '',
          social_tiktok: res.data.shop.social_tiktok || '',
          custom_css: res.data.shop.custom_css || '',
          // Disposition et éléments flottants
          floating_whatsapp: res.data.shop.floating_whatsapp === 1,
          floating_cart: res.data.shop.floating_cart === 1,
          scroll_to_top: res.data.shop.scroll_to_top === 1,
          sticky_categories: res.data.shop.sticky_categories === 1,
          modal_animation: res.data.shop.modal_animation || 'scale',
          floating_position: res.data.shop.floating_position || 'bottom-right'
        }));
        console.log('=== DONNÉES SAUVEGARDÉES ===');
        console.log('Theme:', res.data.shop.theme);
        console.log('Card Style:', res.data.shop.card_style);
        console.log('Button Style:', res.data.shop.button_style);
        console.log('Font Family:', res.data.shop.font_family);
        console.log('Primary Color:', res.data.shop.primary_color);
        console.log('Social Facebook:', res.data.shop.social_facebook);
        console.log('===========================');
        showToast(res.data.message, 'success');
      } else {
        showToast(res.data.message, 'error');
      }
    } catch (err) {
      console.error('Erreur:', err);
      showToast('Erreur lors de l\'enregistrement', 'error');
    } finally {
      setSaving(false);
    }
  };

  const toggleShop = async () => {
    const newStatus = !formData.is_active;
    
    if (newStatus && !shop) {
      showToast('Veuillez d\'abord configurer et enregistrer votre boutique', 'warning');
      return;
    }

    try {
      const res = await api.post('/shop/toggle', { is_active: newStatus ? 1 : 0 });
      if (res.data.success) {
        setFormData(prev => ({ ...prev, is_active: newStatus }));
        setShop(prev => prev ? { ...prev, is_active: newStatus ? 1 : 0 } : null);
        showToast(res.data.message, 'success');
      } else {
        showToast(res.data.message, 'error');
      }
    } catch (err) {
      console.error(err);
      showToast('Erreur lors de la modification', 'error');
    }
  };

  const handleImageUpload = async (type, file) => {
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      showToast('Format non supporté. Utilisez JPG, PNG, GIF ou WebP.', 'error');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      showToast('Image trop lourde. Maximum 5 Mo.', 'error');
      return;
    }

    const setUploading = type === 'logo' ? setUploadingLogo : setUploadingBanner;
    setUploading(true);

    try {
      const formDataUpload = new FormData();
      formDataUpload.append('image', file);
      formDataUpload.append('type', type);

      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/upload_shop.php`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formDataUpload
      });

      const result = await response.json();
      
      if (result.success) {
        setFormData(prev => ({ ...prev, [type]: result.path }));
      } else {
        showToast(result.message || 'Erreur upload', 'error');
      }
    } catch (err) {
      console.error(err);
      showToast('Erreur lors de l\'upload', 'error');
    } finally {
      setUploading(false);
    }
  };

  const getImageUrl = (path) => {
    if (!path) return null;
    if (path.startsWith('http')) return path;
    return `${BACKEND_URL}/${path}`;
  };

  const getPublicUrl = () => {
    if (!formData.slug) return null;
    return `${window.location.origin}/boutique/${formData.slug}`;
  };

  const copyLink = () => {
    const url = getPublicUrl();
    if (url) {
      navigator.clipboard.writeText(url);
      showToast('Lien copié !', 'success');
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  // Accès refusé - pas le module shop
  if (!hasAccess) {
    const reason = accessInfo?.subscription?.plan_code 
      ? `Votre pack actuel (${accessInfo.subscription.plan_nom}) ne donne pas accès à la boutique en ligne. Passez à un pack supérieur pour créer votre boutique !`
      : "Souscrivez à un abonnement incluant la boutique en ligne pour vendre vos produits directement depuis la plateforme.";
    
    return (
      <AccessRestrictionPage
        moduleCode="shop"
        moduleName="Boutique en Ligne"
        moduleIcon="🏪"
        reason={reason}
        userSubscription={accessInfo?.subscription}
        onBack={() => navigate('/workspace')}
      />
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-800">🏪 Ma Boutique</h1>
          <p className="text-gray-500 text-sm">Configurez votre boutique en ligne</p>
        </div>
        {shop && (
          <Link
            to="/workspace/shop/orders"
            className="group relative inline-flex items-center gap-3 px-5 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl hover:from-indigo-700 hover:to-purple-700 transition-all shadow-lg hover:shadow-xl hover:scale-105"
          >
            <div className="flex items-center justify-center w-10 h-10 bg-white/20 rounded-lg">
              <span className="text-xl">📦</span>
            </div>
            <div className="text-left">
              <div className="font-bold">Voir les commandes</div>
              <div className="text-xs text-white/80">Gérer et suivre les commandes</div>
            </div>
            {stats?.pending_orders > 0 && (
              <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold px-2.5 py-1 rounded-full animate-pulse shadow-lg">
                {stats.pending_orders}
              </span>
            )}
          </Link>
        )}
      </div>

      {/* Stats si boutique existe */}
      {shop && stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl shadow p-4">
            <div className="text-2xl mb-2">📦</div>
            <div className="text-xl font-bold text-gray-800">{stats.total_orders || 0}</div>
            <div className="text-xs text-gray-500">Commandes totales</div>
          </div>
          <div className="bg-white rounded-xl shadow p-4">
            <div className="text-2xl mb-2">⏳</div>
            <div className="text-xl font-bold text-amber-600">{stats.pending_orders || 0}</div>
            <div className="text-xs text-gray-500">En attente</div>
          </div>
          <div className="bg-white rounded-xl shadow p-4">
            <div className="text-2xl mb-2">📅</div>
            <div className="text-xl font-bold text-blue-600">{stats.today_orders || 0}</div>
            <div className="text-xs text-gray-500">Aujourd'hui</div>
          </div>
        </div>
      )}

      {/* Section CA avec sélecteur de période */}
      {shop && stats && (
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl shadow-sm border border-green-100 p-5 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            {/* Montant CA */}
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-green-100 rounded-xl flex items-center justify-center">
                <span className="text-3xl">💰</span>
              </div>
              <div>
                <div className="text-sm text-green-700 font-medium">Chiffre d'affaires (livrées)</div>
                <div className="text-3xl font-bold text-green-600">
                  {loadingRevenue ? (
                    <span className="inline-block w-24 h-8 bg-green-200 rounded animate-pulse"></span>
                  ) : (
                    <>{parseInt(revenueAmount || 0).toLocaleString()} <span className="text-lg font-normal">FCFA</span></>
                  )}
                </div>
              </div>
            </div>
            
            {/* Sélecteur de période */}
            <div className="flex flex-col sm:flex-row gap-3">
              {/* Boutons de période rapide */}
              <div className="flex flex-wrap gap-2">
                {[
                  { value: 'all', label: 'Tout' },
                  { value: 'today', label: "Aujourd'hui" },
                  { value: 'week', label: 'Cette semaine' },
                  { value: 'month', label: 'Ce mois' },
                  { value: 'year', label: 'Cette année' },
                  { value: 'custom', label: '📅 Personnalisé' }
                ].map(period => (
                  <button
                    key={period.value}
                    onClick={() => setRevenuePeriod(period.value)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${
                      revenuePeriod === period.value
                        ? 'bg-green-600 text-white shadow-md'
                        : 'bg-white text-gray-600 hover:bg-green-100 border border-green-200'
                    }`}
                  >
                    {period.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
          
          {/* Sélecteur de dates personnalisées */}
          {revenuePeriod === 'custom' && (
            <div className="mt-4 pt-4 border-t border-green-200">
              <div className="flex flex-col sm:flex-row items-start sm:items-end gap-3">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-green-700 mb-1">Date de début</label>
                  <input
                    type="date"
                    value={customDateStart}
                    onChange={(e) => setCustomDateStart(e.target.value)}
                    className="w-full px-3 py-2 border border-green-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-sm font-medium text-green-700 mb-1">Date de fin</label>
                  <input
                    type="date"
                    value={customDateEnd}
                    onChange={(e) => setCustomDateEnd(e.target.value)}
                    className="w-full px-3 py-2 border border-green-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  />
                </div>
                <button
                  onClick={() => loadRevenueByPeriod('custom', customDateStart, customDateEnd)}
                  disabled={!customDateStart || !customDateEnd || loadingRevenue}
                  className="px-5 py-2 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
                >
                  {loadingRevenue ? '...' : '🔍 Calculer'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Statut de la boutique */}
      <div className={`mb-6 rounded-xl p-4 ${formData.is_active ? 'bg-green-50 border border-green-200' : 'bg-gray-50 border border-gray-200'}`}>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className={`w-4 h-4 rounded-full ${formData.is_active ? 'bg-green-500 animate-pulse' : 'bg-gray-400'}`}></div>
            <div>
              <div className="font-medium text-gray-800">
                {formData.is_active ? '🟢 Boutique en ligne' : '🔴 Boutique hors ligne'}
              </div>
              {formData.is_active && getPublicUrl() && (
                <div className="text-sm text-gray-600 mt-1 flex items-center gap-2 flex-wrap">
                  <span className="break-all">{getPublicUrl()}</span>
                  <button
                    onClick={copyLink}
                    className="text-indigo-600 hover:text-indigo-800 text-sm"
                  >
                    📋 Copier
                  </button>
                  <a
                    href={getPublicUrl()}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-indigo-600 hover:text-indigo-800 text-sm"
                  >
                    🔗 Ouvrir
                  </a>
                </div>
              )}
            </div>
          </div>
          <button
            onClick={toggleShop}
            className={`px-4 py-2 rounded-lg font-medium transition ${
              formData.is_active
                ? 'bg-red-100 text-red-600 hover:bg-red-200'
                : 'bg-green-600 text-white hover:bg-green-700'
            }`}
          >
            {formData.is_active ? 'Désactiver' : 'Activer la boutique'}
          </button>
        </div>
      </div>

      {/* Onglets de navigation */}
      <div className="bg-white rounded-xl shadow-sm mb-6 overflow-x-auto">
        <div className="flex border-b min-w-max">
          {[
            { id: 'general', label: '📝 Général', icon: '📝' },
            { id: 'design', label: '🎨 Design', icon: '🎨' },
            { id: 'colors', label: '🌈 Couleurs', icon: '🌈' },
            { id: 'layout', label: '📐 Mise en page', icon: '📐' },
            { id: 'delivery', label: '🚚 Livraison', icon: '🚚' },
            { id: 'social', label: '📱 Réseaux', icon: '📱' }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-3 font-medium text-sm whitespace-nowrap transition border-b-2 ${
                activeTab === tab.id
                  ? 'border-indigo-600 text-indigo-600 bg-indigo-50'
                  : 'border-transparent text-gray-600 hover:text-gray-800 hover:bg-gray-50'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Formulaire */}
      <form onSubmit={handleSubmit}>
        
        {/* TAB: Général */}
        {activeTab === 'general' && (
          <>
            <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
              <h2 className="text-lg font-semibold text-gray-800 mb-4">Informations de base</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Nom */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nom de la boutique *
                  </label>
                  <input
                    type="text"
                    name="nom"
                    value={formData.nom}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Ma Super Boutique"
                    required
                  />
                </div>

                {/* Slug */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    URL de la boutique *
                  </label>
                  <div className="flex items-center">
                    <span className="text-gray-500 text-sm mr-1">/boutique/</span>
                    <input
                      type="text"
                      name="slug"
                      value={formData.slug}
                      onChange={handleChange}
                      className={`flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                        slugAvailable === true ? 'border-green-500' : slugAvailable === false ? 'border-red-500' : ''
                      }`}
                      placeholder="ma-boutique"
                      required
                    />
                  </div>
                  <div className="mt-1 text-xs">
                    {slugChecking && <span className="text-gray-500">Vérification...</span>}
                    {!slugChecking && slugAvailable === true && <span className="text-green-600">✓ Disponible</span>}
                    {!slugChecking && slugAvailable === false && <span className="text-red-600">✗ Déjà utilisé</span>}
                  </div>
                </div>

                {/* Description */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    rows={3}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Décrivez votre boutique..."
                  />
                </div>
              </div>
            </div>

            {/* Images */}
            <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
              <h2 className="text-lg font-semibold text-gray-800 mb-4">Images</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Logo */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Logo</label>
                  <div className="flex items-center gap-4">
                    <div className="w-20 h-20 rounded-xl bg-gray-100 flex items-center justify-center overflow-hidden">
                      {formData.logo ? (
                        <img src={getImageUrl(formData.logo)} alt="Logo" className="w-full h-full object-cover" />
                      ) : (
                        <span className="text-3xl">🏪</span>
                      )}
                    </div>
                    <div>
                      <input
                        type="file"
                        ref={logoInputRef}
                        onChange={(e) => handleImageUpload('logo', e.target.files[0])}
                        accept="image/*"
                        className="hidden"
                      />
                      <button
                        type="button"
                        onClick={() => logoInputRef.current?.click()}
                        disabled={uploadingLogo}
                        className="px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm transition"
                      >
                        {uploadingLogo ? 'Chargement...' : 'Changer le logo'}
                      </button>
                      {formData.logo && (
                        <button
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, logo: '' }))}
                          className="ml-2 text-red-600 hover:text-red-800 text-sm"
                        >
                          Supprimer
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Bannière */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Bannière</label>
                  <div className="relative">
                    <div className="w-full h-32 rounded-xl bg-gray-100 flex items-center justify-center overflow-hidden">
                      {formData.banner ? (
                        <img src={getImageUrl(formData.banner)} alt="Bannière" className="w-full h-full object-cover" />
                      ) : (
                        <span className="text-gray-400">Aucune bannière</span>
                      )}
                    </div>
                    <div className="mt-2">
                      <input
                        type="file"
                        ref={bannerInputRef}
                        onChange={(e) => handleImageUpload('banner', e.target.files[0])}
                        accept="image/*"
                        className="hidden"
                      />
                      <button
                        type="button"
                        onClick={() => bannerInputRef.current?.click()}
                        disabled={uploadingBanner}
                        className="px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm transition"
                      >
                        {uploadingBanner ? 'Chargement...' : 'Changer la bannière'}
                      </button>
                      {formData.banner && (
                        <button
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, banner: '' }))}
                          className="ml-2 text-red-600 hover:text-red-800 text-sm"
                        >
                          Supprimer
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact */}
            <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
              <h2 className="text-lg font-semibold text-gray-800 mb-4">Contact</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Téléphone
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="+225 77 123 45 67"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    WhatsApp
                  </label>
                  <input
                    type="tel"
                    name="whatsapp"
                    value={formData.whatsapp}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="+225 77 123 45 67"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Devise
                  </label>
                  <select
                    name="currency"
                    value={formData.currency}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="FCFA">FCFA</option>
                    <option value="XOF">XOF</option>
                    <option value="EUR">EUR</option>
                    <option value="USD">USD</option>
                  </select>
                </div>

                <div className="md:col-span-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Adresse
                  </label>
                  <textarea
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    rows={2}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Adresse de votre boutique..."
                  />
                </div>
              </div>
            </div>
          </>
        )}

        {/* TAB: Design */}
        {activeTab === 'design' && (
          <>
            {/* Thèmes prédéfinis */}
            <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
              <h2 className="text-lg font-semibold text-gray-800 mb-4">🎨 Thèmes prédéfinis</h2>
              <p className="text-sm text-gray-500 mb-4">Choisissez un thème pour appliquer automatiquement les couleurs</p>
              
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {THEMES.map(theme => (
                  <button
                    key={theme.id}
                    type="button"
                    onClick={() => applyTheme(theme.id)}
                    className={`p-4 rounded-xl border-2 transition ${
                      formData.theme === theme.id 
                        ? 'border-indigo-600 bg-indigo-50' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    {theme.colors ? (
                      <div className="flex gap-1 mb-2 justify-center">
                        <div className="w-6 h-6 rounded-full" style={{ backgroundColor: theme.colors.primary }}></div>
                        <div className="w-6 h-6 rounded-full" style={{ backgroundColor: theme.colors.secondary }}></div>
                        <div className="w-6 h-6 rounded-full" style={{ backgroundColor: theme.colors.accent }}></div>
                      </div>
                    ) : (
                      <div className="flex gap-1 mb-2 justify-center">
                        <div className="w-6 h-6 rounded-full bg-gradient-to-r from-red-500 via-green-500 to-blue-500"></div>
                      </div>
                    )}
                    <div className="text-sm font-medium text-gray-800">{theme.name}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Police */}
            <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
              <h2 className="text-lg font-semibold text-gray-800 mb-4">✒️ Police d'écriture</h2>
              
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {FONTS.map(font => (
                  <button
                    key={font.id}
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, font_family: font.id }))}
                    className={`p-3 rounded-lg border-2 transition ${
                      formData.font_family === font.id 
                        ? 'border-indigo-600 bg-indigo-50' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    style={{ fontFamily: font.id }}
                  >
                    <div className="text-lg font-bold mb-1">Aa</div>
                    <div className="text-xs text-gray-600">{font.name}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Style des cartes */}
            <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
              <h2 className="text-lg font-semibold text-gray-800 mb-4">🃏 Style des cartes produits</h2>
              
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {CARD_STYLES.map(style => (
                  <button
                    key={style.id}
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, card_style: style.id }))}
                    className={`p-4 border-2 transition ${
                      formData.card_style === style.id 
                        ? 'border-indigo-600' 
                        : 'border-gray-200 hover:border-gray-300'
                    } ${style.preview}`}
                  >
                    <div className={`w-full h-16 bg-gray-200 mb-2 ${style.preview}`}></div>
                    <div className="text-sm font-medium">{style.name}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Style des boutons */}
            <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
              <h2 className="text-lg font-semibold text-gray-800 mb-4">🔘 Style des boutons</h2>
              
              <div className="grid grid-cols-3 gap-4">
                {BUTTON_STYLES.map(style => (
                  <button
                    key={style.id}
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, button_style: style.id }))}
                    className={`p-4 border-2 transition rounded-lg ${
                      formData.button_style === style.id 
                        ? 'border-indigo-600 bg-indigo-50' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div 
                      className={`px-4 py-2 text-white text-sm font-medium ${style.preview}`}
                      style={{ backgroundColor: formData.primary_color }}
                    >
                      Bouton
                    </div>
                    <div className="text-sm mt-2">{style.name}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Style du header */}
            <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
              <h2 className="text-lg font-semibold text-gray-800 mb-4">🖼️ Style de l'en-tête</h2>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {HEADER_STYLES.map(style => (
                  <button
                    key={style.id}
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, header_style: style.id }))}
                    className={`p-4 border-2 transition rounded-xl text-left ${
                      formData.header_style === style.id 
                        ? 'border-indigo-600 bg-indigo-50' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="font-medium text-gray-800">{style.name}</div>
                    <div className="text-sm text-gray-500">{style.desc}</div>
                  </button>
                ))}
              </div>
            </div>
          </>
        )}

        {/* TAB: Couleurs */}
        {activeTab === 'colors' && (
          <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">🌈 Couleurs personnalisées</h2>
            <p className="text-sm text-gray-500 mb-6">
              Personnalisez les couleurs de votre boutique pour correspondre à votre marque
            </p>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Couleur principale */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Couleur principale
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    name="primary_color"
                    value={formData.primary_color}
                    onChange={handleChange}
                    className="w-12 h-12 rounded-lg cursor-pointer border-2 border-gray-200"
                  />
                  <input
                    type="text"
                    value={formData.primary_color}
                    onChange={(e) => setFormData(prev => ({ ...prev, primary_color: e.target.value }))}
                    className="flex-1 px-3 py-2 border rounded-lg font-mono text-sm"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">Boutons, liens, éléments principaux</p>
              </div>

              {/* Couleur secondaire */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Couleur secondaire
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    name="secondary_color"
                    value={formData.secondary_color}
                    onChange={handleChange}
                    className="w-12 h-12 rounded-lg cursor-pointer border-2 border-gray-200"
                  />
                  <input
                    type="text"
                    value={formData.secondary_color}
                    onChange={(e) => setFormData(prev => ({ ...prev, secondary_color: e.target.value }))}
                    className="flex-1 px-3 py-2 border rounded-lg font-mono text-sm"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">Accents, survols</p>
              </div>

              {/* Couleur d'accentuation */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Couleur d'accentuation
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    name="accent_color"
                    value={formData.accent_color}
                    onChange={handleChange}
                    className="w-12 h-12 rounded-lg cursor-pointer border-2 border-gray-200"
                  />
                  <input
                    type="text"
                    value={formData.accent_color}
                    onChange={(e) => setFormData(prev => ({ ...prev, accent_color: e.target.value }))}
                    className="flex-1 px-3 py-2 border rounded-lg font-mono text-sm"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">Prix, badges, succès</p>
              </div>

              {/* Couleur du texte */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Couleur du texte
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    name="text_color"
                    value={formData.text_color}
                    onChange={handleChange}
                    className="w-12 h-12 rounded-lg cursor-pointer border-2 border-gray-200"
                  />
                  <input
                    type="text"
                    value={formData.text_color}
                    onChange={(e) => setFormData(prev => ({ ...prev, text_color: e.target.value }))}
                    className="flex-1 px-3 py-2 border rounded-lg font-mono text-sm"
                  />
                </div>
              </div>

              {/* Couleur de fond */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Couleur de fond
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    name="background_color"
                    value={formData.background_color}
                    onChange={handleChange}
                    className="w-12 h-12 rounded-lg cursor-pointer border-2 border-gray-200"
                  />
                  <input
                    type="text"
                    value={formData.background_color}
                    onChange={(e) => setFormData(prev => ({ ...prev, background_color: e.target.value }))}
                    className="flex-1 px-3 py-2 border rounded-lg font-mono text-sm"
                  />
                </div>
              </div>
            </div>

            {/* Aperçu des couleurs */}
            <div className="mt-8 p-6 rounded-xl" style={{ backgroundColor: formData.background_color }}>
              <h3 className="font-bold mb-4" style={{ color: formData.text_color }}>Aperçu des couleurs</h3>
              <div className="flex flex-wrap gap-3">
                <button 
                  className="px-4 py-2 rounded-lg text-white font-medium"
                  style={{ backgroundColor: formData.primary_color }}
                >
                  Bouton principal
                </button>
                <button 
                  className="px-4 py-2 rounded-lg text-white font-medium"
                  style={{ backgroundColor: formData.secondary_color }}
                >
                  Bouton secondaire
                </button>
                <span 
                  className="px-4 py-2 rounded-lg font-bold"
                  style={{ backgroundColor: formData.accent_color, color: '#fff' }}
                >
                  25 000 FCFA
                </span>
              </div>
            </div>
          </div>
        )}

        {/* TAB: Mise en page */}
        {activeTab === 'layout' && (
          <>
            {/* Grille produits */}
            <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
              <h2 className="text-lg font-semibold text-gray-800 mb-4">📐 Grille des produits</h2>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nombre de colonnes (desktop)
                </label>
                <div className="flex gap-2">
                  {[2, 3, 4, 5, 6].map(cols => (
                    <button
                      key={cols}
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, product_grid_cols: cols }))}
                      className={`w-12 h-12 rounded-lg border-2 font-bold transition ${
                        formData.product_grid_cols === cols
                          ? 'border-indigo-600 bg-indigo-600 text-white'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      {cols}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Éléments à afficher */}
            <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
              <h2 className="text-lg font-semibold text-gray-800 mb-4">👁️ Éléments visibles</h2>
              
              <div className="space-y-4">
                {[
                  { name: 'show_banner', label: 'Afficher la bannière', desc: 'Image d\'en-tête de la boutique' },
                  { name: 'show_description', label: 'Afficher la description', desc: 'Texte de présentation' },
                  { name: 'show_contact', label: 'Afficher les contacts', desc: 'Téléphone, WhatsApp, adresse' },
                  { name: 'show_search', label: 'Afficher la recherche', desc: 'Barre de recherche produits' },
                  { name: 'show_category_filter', label: 'Afficher les filtres', desc: 'Filtres par catégorie' },
                  { name: 'show_stock', label: 'Afficher le stock', desc: 'Quantité disponible' }
                ].map(item => (
                  <div key={item.name} className="flex items-center justify-between py-2 border-b">
                    <div>
                      <div className="font-medium text-gray-800">{item.label}</div>
                      <div className="text-sm text-gray-500">{item.desc}</div>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        name={item.name}
                        checked={formData[item.name]}
                        onChange={handleChange}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                    </label>
                  </div>
                ))}
              </div>
            </div>

            {/* 🎨 TEMPLATES COMPLETS */}
            <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl shadow-sm p-6 mb-6 border border-indigo-100">
              <h2 className="text-lg font-semibold text-gray-800 mb-2 flex items-center gap-2">
                <span className="text-2xl">🎨</span> Templates Prêts à l'emploi
              </h2>
              <p className="text-sm text-gray-500 mb-4">
                Choisissez un design complet et personnalisez-le à votre goût
              </p>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  {
                    name: '🛍️ Boutique Classique',
                    desc: 'Design épuré et professionnel',
                    preview: 'bg-white border',
                    config: {
                      primary_color: '#4F46E5',
                      secondary_color: '#7C3AED',
                      accent_color: '#EC4899',
                      header_style: 'classic',
                      card_style: 'rounded-xl',
                      button_style: 'rounded-lg',
                      custom_css: `.product-card { background: white; border-radius: 12px; box-shadow: 0 4px 20px rgba(0,0,0,0.08); transition: all 0.3s ease; }\n.product-card:hover { transform: translateY(-4px); box-shadow: 0 12px 30px rgba(0,0,0,0.12); }\n.shop-btn { background: linear-gradient(135deg, #4F46E5, #7C3AED); border-radius: 8px; }\n.shop-btn:hover { transform: scale(1.02); box-shadow: 0 8px 20px rgba(79,70,229,0.3); }`
                    }
                  },
                  {
                    name: '🌙 Dark Mode Élégant',
                    desc: 'Style sombre et moderne',
                    preview: 'bg-gray-900',
                    config: {
                      primary_color: '#8B5CF6',
                      secondary_color: '#EC4899',
                      accent_color: '#F59E0B',
                      header_style: 'hero',
                      custom_css: `.product-card { background: linear-gradient(145deg, #1f2937, #111827); border: 1px solid #374151; border-radius: 16px; }\n.product-card:hover { border-color: #8B5CF6; box-shadow: 0 0 30px rgba(139,92,246,0.2); }\n.product-card h3, .product-card p, .product-card span { color: #f9fafb !important; }\n.shop-btn { background: linear-gradient(90deg, #8B5CF6, #EC4899); border-radius: 12px; }\n.shop-btn:hover { box-shadow: 0 0 25px rgba(139,92,246,0.5); }`
                    }
                  },
                  {
                    name: '🌸 Pastel Dreams',
                    desc: 'Doux et féminin',
                    preview: 'bg-gradient-to-br from-pink-100 to-purple-100',
                    config: {
                      primary_color: '#EC4899',
                      secondary_color: '#A855F7',
                      accent_color: '#F472B6',
                      custom_css: `.product-card { background: linear-gradient(145deg, #fdf2f8, #faf5ff); border: 2px solid #f9a8d4; border-radius: 24px; }\n.product-card:hover { transform: translateY(-6px) rotate(1deg); box-shadow: 0 20px 40px rgba(236,72,153,0.15); }\n.shop-btn { background: linear-gradient(90deg, #EC4899, #A855F7); border-radius: 50px; }\n.shop-btn:hover { transform: scale(1.05); }`
                    }
                  },
                  {
                    name: '🔥 Néon Cyberpunk',
                    desc: 'Futuriste et audacieux',
                    preview: 'bg-slate-900 border-2 border-cyan-400',
                    config: {
                      primary_color: '#22D3EE',
                      secondary_color: '#F43F5E',
                      accent_color: '#FBBF24',
                      custom_css: `.product-card { background: #0f172a; border: 2px solid #22d3ee; border-radius: 0; box-shadow: 0 0 20px rgba(34,211,238,0.3); }\n.product-card:hover { box-shadow: 0 0 40px rgba(34,211,238,0.5); border-color: #f43f5e; }\n.product-card h3, .product-card p, .product-card span { color: #22d3ee !important; }\n.shop-btn { background: transparent; border: 2px solid #22d3ee; color: #22d3ee; }\n.shop-btn:hover { background: #22d3ee; color: #0f172a; }`
                    }
                  },
                  {
                    name: '🌿 Eco Nature',
                    desc: 'Bio et naturel',
                    preview: 'bg-gradient-to-br from-green-100 to-emerald-100',
                    config: {
                      primary_color: '#10B981',
                      secondary_color: '#059669',
                      accent_color: '#84CC16',
                      custom_css: `.product-card { background: linear-gradient(145deg, #ecfdf5, #f0fdf4); border: 2px solid #a7f3d0; border-radius: 20px; }\n.product-card:hover { transform: translateY(-5px); box-shadow: 0 15px 30px rgba(16,185,129,0.15); }\n.shop-btn { background: linear-gradient(90deg, #10B981, #059669); border-radius: 50px; }\n.shop-btn:hover { box-shadow: 0 0 25px rgba(16,185,129,0.4); }`
                    }
                  },
                  {
                    name: '✨ Luxe Premium',
                    desc: 'Haut de gamme',
                    preview: 'bg-gradient-to-br from-amber-100 to-yellow-100 border border-amber-300',
                    config: {
                      primary_color: '#D97706',
                      secondary_color: '#B45309',
                      accent_color: '#FBBF24',
                      custom_css: `.product-card { background: linear-gradient(145deg, #fffbeb, #fef3c7); border: 2px solid #d97706; border-radius: 4px; }\n.product-card:hover { box-shadow: 0 0 30px rgba(217,119,6,0.25); }\n.shop-btn { background: linear-gradient(90deg, #D97706, #B45309); border-radius: 2px; letter-spacing: 1px; text-transform: uppercase; }\n.shop-btn:hover { box-shadow: 0 0 20px rgba(217,119,6,0.5); }`
                    }
                  },
                  {
                    name: '🎮 Gaming Zone',
                    desc: 'Pour les gamers',
                    preview: 'bg-gray-900 border-2 border-red-500',
                    config: {
                      primary_color: '#EF4444',
                      secondary_color: '#7C3AED',
                      accent_color: '#22D3EE',
                      custom_css: `.product-card { background: linear-gradient(145deg, #18181b, #27272a); border: 2px solid #ef4444; border-radius: 8px; }\n.product-card:hover { transform: scale(1.03); box-shadow: 0 0 30px rgba(239,68,68,0.3); }\n.product-card h3, .product-card p, .product-card span { color: #fafafa !important; }\n.shop-btn { background: linear-gradient(135deg, #EF4444, #7C3AED); border-radius: 8px; text-transform: uppercase; }\n.shop-btn:hover { animation: pulse 1s infinite; }\n@keyframes pulse { 0%,100% { box-shadow: 0 0 20px rgba(239,68,68,0.5); } 50% { box-shadow: 0 0 40px rgba(124,58,237,0.5); } }`
                    }
                  },
                  {
                    name: '🏖️ Summer Vibes',
                    desc: 'Frais et estival',
                    preview: 'bg-gradient-to-br from-orange-200 to-cyan-200',
                    config: {
                      primary_color: '#F97316',
                      secondary_color: '#06B6D4',
                      accent_color: '#FBBF24',
                      custom_css: `.product-card { background: linear-gradient(135deg, #fff7ed, #ecfeff); border: none; border-radius: 24px; box-shadow: 0 10px 40px rgba(249,115,22,0.1); }\n.product-card:hover { transform: translateY(-8px) rotate(2deg); box-shadow: 0 20px 50px rgba(249,115,22,0.2); }\n.shop-btn { background: linear-gradient(90deg, #F97316, #06B6D4); border-radius: 50px; }\n.shop-btn:hover { transform: scale(1.1); }`
                    }
                  }
                ].map((template, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => {
                      setFormData(prev => ({ ...prev, ...template.config }));
                      showToast(`Template "${template.name}" appliqué !`, 'success');
                    }}
                    className="group relative p-4 rounded-xl border-2 border-gray-200 hover:border-indigo-400 hover:shadow-lg transition-all text-left bg-white overflow-hidden"
                  >
                    <div className={`absolute inset-0 opacity-5 ${template.preview}`}></div>
                    <div className="relative">
                      <div className={`h-16 rounded-lg mb-3 ${template.preview} flex items-center justify-center gap-1`}>
                        <div className="w-6 h-8 bg-white/60 rounded shadow-sm"></div>
                        <div className="w-6 h-8 bg-white/60 rounded shadow-sm"></div>
                        <div className="w-6 h-8 bg-white/60 rounded shadow-sm"></div>
                      </div>
                      <h3 className="font-semibold text-gray-800 group-hover:text-indigo-600 text-sm">{template.name}</h3>
                      <p className="text-xs text-gray-500">{template.desc}</p>
                    </div>
                    <span className="absolute top-2 right-2 bg-indigo-600 text-white text-[10px] px-2 py-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
                      Appliquer
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* 📐 DISPOSITION & ÉLÉMENTS FLOTTANTS */}
            <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
              <h2 className="text-lg font-semibold text-gray-800 mb-2 flex items-center gap-2">
                <span className="text-2xl">📐</span> Disposition & Éléments Flottants
              </h2>
              <p className="text-sm text-gray-500 mb-4">
                Ajoutez des éléments interactifs et personnalisez la navigation
              </p>
              
              {/* Options de disposition */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                {/* Bouton WhatsApp */}
                <div className="p-4 border rounded-xl hover:border-gray-300 transition-colors">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-gray-700 text-sm">💬 Bouton WhatsApp</span>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        name="floating_whatsapp"
                        checked={formData.floating_whatsapp || false}
                        onChange={handleChange}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-500"></div>
                    </label>
                  </div>
                  <p className="text-xs text-gray-500">Bouton en bas pour contacter</p>
                </div>

                {/* Panier flottant */}
                <div className="p-4 border rounded-xl hover:border-gray-300 transition-colors">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-gray-700 text-sm">🛒 Panier flottant</span>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        name="floating_cart"
                        checked={formData.floating_cart !== false}
                        onChange={handleChange}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                    </label>
                  </div>
                  <p className="text-xs text-gray-500">Mini panier toujours visible</p>
                </div>

                {/* Retour en haut */}
                <div className="p-4 border rounded-xl hover:border-gray-300 transition-colors">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-gray-700 text-sm">⬆️ Retour en haut</span>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        name="scroll_to_top"
                        checked={formData.scroll_to_top !== false}
                        onChange={handleChange}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                    </label>
                  </div>
                  <p className="text-xs text-gray-500">Bouton après défilement</p>
                </div>

                {/* Catégories épinglées */}
                <div className="p-4 border rounded-xl hover:border-gray-300 transition-colors">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-gray-700 text-sm">📌 Catégories épinglées</span>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        name="sticky_categories"
                        checked={formData.sticky_categories !== false}
                        onChange={handleChange}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                    </label>
                  </div>
                  <p className="text-xs text-gray-500">Filtres toujours visibles</p>
                </div>
              </div>

              {/* Animation des modals */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-3">🎬 Animation d'ouverture</label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {[
                    { value: 'fade', name: 'Fondu', icon: '✨' },
                    { value: 'slide-up', name: 'Glisser', icon: '⬆️' },
                    { value: 'scale', name: 'Zoom', icon: '🔍' },
                    { value: 'bounce', name: 'Rebond', icon: '⚡' },
                  ].map(anim => (
                    <button
                      key={anim.value}
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, modal_animation: anim.value }))}
                      className={`p-3 rounded-xl border-2 transition-all text-center ${
                        formData.modal_animation === anim.value ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <span className="text-xl block mb-1">{anim.icon}</span>
                      <span className="text-xs font-medium text-gray-700">{anim.name}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Position flottants */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">📍 Position des boutons</label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {[
                    { value: 'bottom-right', name: 'Bas droite', icon: '↘' },
                    { value: 'bottom-left', name: 'Bas gauche', icon: '↙' },
                    { value: 'bottom-center', name: 'Bas centre', icon: '↓' },
                    { value: 'side-right', name: 'Côté droit', icon: '→' },
                  ].map(pos => (
                    <button
                      key={pos.value}
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, floating_position: pos.value }))}
                      className={`p-3 rounded-xl border-2 transition-all text-center ${
                        (formData.floating_position || 'bottom-right') === pos.value ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <span className="text-xl block mb-1">{pos.icon}</span>
                      <span className="text-xs font-medium text-gray-700">{pos.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Bandeau d'annonce */}
            <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
              <h2 className="text-lg font-semibold text-gray-800 mb-4">📢 Bandeau d'annonce</h2>
              <p className="text-sm text-gray-500 mb-4">
                Affichez un message important en haut de votre boutique (promotions, nouveautés...)
              </p>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Texte de l'annonce
                  </label>
                  <input
                    type="text"
                    name="announcement_text"
                    value={formData.announcement_text}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="🎉 Livraison gratuite dès 50 000 FCFA !"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Couleur du bandeau
                  </label>
                  <div className="flex items-center gap-3">
                    <input
                      type="color"
                      name="announcement_bg_color"
                      value={formData.announcement_bg_color}
                      onChange={handleChange}
                      className="w-12 h-12 rounded-lg cursor-pointer border-2 border-gray-200"
                    />
                    <div className="flex gap-2">
                      {['#FEF3C7', '#DBEAFE', '#D1FAE5', '#FEE2E2', '#E9D5FF'].map(color => (
                        <button
                          key={color}
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, announcement_bg_color: color }))}
                          className={`w-8 h-8 rounded-full border-2 ${
                            formData.announcement_bg_color === color ? 'border-gray-800' : 'border-transparent'
                          }`}
                          style={{ backgroundColor: color }}
                        />
                      ))}
                    </div>
                  </div>
                </div>

                {/* Aperçu */}
                {formData.announcement_text && (
                  <div 
                    className="p-3 rounded-lg text-center font-medium"
                    style={{ backgroundColor: formData.announcement_bg_color }}
                  >
                    {formData.announcement_text}
                  </div>
                )}
              </div>
            </div>

            {/* CSS personnalisé */}
            <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
              <h2 className="text-lg font-semibold text-gray-800 mb-4">💻 CSS personnalisé (avancé)</h2>
              
              {/* Catégories de styles */}
              {(() => {
                const cssCategories = [
                  {
                    name: '🎴 Cartes Produits',
                    styles: [
                      { name: '🎯 Minimaliste', preview: 'bg-gray-50 border border-gray-200', css: `.product-card { border: 1px solid #e5e7eb; border-radius: 8px; transition: transform 0.2s ease; }\n.product-card:hover { transform: translateY(-2px); }` },
                      { name: '🌊 Ombre douce', preview: 'bg-white shadow-lg', css: `.product-card { box-shadow: 0 10px 40px rgba(0,0,0,0.08); border-radius: 16px; transition: box-shadow 0.3s ease; }\n.product-card:hover { box-shadow: 0 20px 60px rgba(0,0,0,0.12); }` },
                      { name: '🔥 Néon Rose', preview: 'bg-gray-900 border-2 border-pink-500', css: `.product-card { background: #1a1a2e; border: 2px solid #ff6b9d; border-radius: 12px; box-shadow: 0 0 20px rgba(255,107,157,0.3); }\n.product-card:hover { box-shadow: 0 0 30px rgba(255,107,157,0.5); transform: scale(1.02); }` },
                      { name: '💎 Glassmorphism', preview: 'bg-white/60 backdrop-blur border border-white/30', css: `.product-card { background: rgba(255,255,255,0.7); backdrop-filter: blur(10px); border: 1px solid rgba(255,255,255,0.3); border-radius: 20px; }\n.product-card:hover { background: rgba(255,255,255,0.9); transform: translateY(-5px); }` },
                      { name: '🌈 Gradient', preview: 'bg-gradient-to-br from-purple-500 to-pink-500', css: `.product-card { background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); border-radius: 16px; }\n.product-card:hover { transform: rotate(1deg) scale(1.03); box-shadow: 0 15px 35px rgba(102,126,234,0.4); }` },
                      { name: '⚡ Bounce', preview: 'bg-yellow-100 border-2 border-yellow-400', css: `.product-card { border: 2px solid #fbbf24; border-radius: 12px; transition: all 0.3s cubic-bezier(0.68, -0.55, 0.265, 1.55); }\n.product-card:hover { transform: scale(1.05); box-shadow: 0 10px 25px rgba(251,191,36,0.3); }` },
                      { name: '🖤 Dark Elite', preview: 'bg-gray-900 border border-gray-700', css: `.product-card { background: #111827; border: 1px solid #374151; border-radius: 12px; color: #f9fafb; }\n.product-card:hover { border-color: #6366f1; box-shadow: 0 0 30px rgba(99,102,241,0.2); }` },
                      { name: '🌸 Pastel', preview: 'bg-pink-50 border-2 border-pink-200', css: `.product-card { background: linear-gradient(145deg, #fdf2f8, #fce7f3); border: 2px solid #fbcfe8; border-radius: 24px; }\n.product-card:hover { transform: translateY(-4px); box-shadow: 0 12px 24px rgba(244,114,182,0.2); }` },
                    ]
                  },
                  {
                    name: '🔘 Boutons',
                    styles: [
                      { name: '💫 Glow', preview: 'bg-blue-500 shadow-lg shadow-blue-500/50', css: `.shop-btn { background: #3b82f6; box-shadow: 0 0 20px rgba(59,130,246,0.5); transition: all 0.3s ease; }\n.shop-btn:hover { box-shadow: 0 0 30px rgba(59,130,246,0.8); transform: translateY(-2px); }` },
                      { name: '🌙 Morphing', preview: 'bg-gradient-to-r from-indigo-500 to-purple-500', css: `.shop-btn { background: linear-gradient(90deg, #6366f1, #a855f7); background-size: 200% 100%; transition: all 0.5s ease; }\n.shop-btn:hover { background-position: 100% 0; transform: scale(1.05); }` },
                      { name: '⬛ Outline', preview: 'bg-transparent border-2 border-gray-800', css: `.shop-btn { background: transparent; border: 2px solid #1f2937; color: #1f2937; transition: all 0.3s ease; }\n.shop-btn:hover { background: #1f2937; color: white; }` },
                      { name: '🔴 Pulse', preview: 'bg-red-500 animate-pulse', css: `.shop-btn { background: #ef4444; animation: pulse 2s infinite; }\n@keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.7; } }` },
                      { name: '🟢 Success', preview: 'bg-gradient-to-r from-green-400 to-emerald-500', css: `.shop-btn { background: linear-gradient(90deg, #4ade80, #10b981); border-radius: 50px; transition: all 0.3s ease; }\n.shop-btn:hover { transform: scale(1.1); box-shadow: 0 10px 20px rgba(16,185,129,0.3); }` },
                      { name: '🟡 Golden', preview: 'bg-gradient-to-r from-yellow-400 to-orange-500', css: `.shop-btn { background: linear-gradient(90deg, #fbbf24, #f97316); color: #1f2937; font-weight: bold; }\n.shop-btn:hover { box-shadow: 0 0 25px rgba(251,191,36,0.6); transform: translateY(-3px); }` },
                    ]
                  },
                  {
                    name: '✨ Animations Entrée',
                    styles: [
                      { name: '📥 Fade In', preview: 'bg-blue-100', css: `.product-card { animation: fadeIn 0.6s ease-out forwards; opacity: 0; }\n@keyframes fadeIn { to { opacity: 1; } }` },
                      { name: '⬆️ Slide Up', preview: 'bg-green-100', css: `.product-card { animation: slideUp 0.5s ease-out forwards; opacity: 0; transform: translateY(30px); }\n@keyframes slideUp { to { opacity: 1; transform: translateY(0); } }` },
                      { name: '📐 Scale In', preview: 'bg-purple-100', css: `.product-card { animation: scaleIn 0.4s ease-out forwards; transform: scale(0.8); opacity: 0; }\n@keyframes scaleIn { to { transform: scale(1); opacity: 1; } }` },
                      { name: '🔄 Rotate In', preview: 'bg-orange-100', css: `.product-card { animation: rotateIn 0.6s ease-out forwards; transform: rotate(-10deg) scale(0.9); opacity: 0; }\n@keyframes rotateIn { to { transform: rotate(0) scale(1); opacity: 1; } }` },
                      { name: '💨 Blur In', preview: 'bg-cyan-100', css: `.product-card { animation: blurIn 0.5s ease-out forwards; filter: blur(10px); opacity: 0; }\n@keyframes blurIn { to { filter: blur(0); opacity: 1; } }` },
                      { name: '🌀 Spiral', preview: 'bg-pink-100', css: `.product-card { animation: spiral 0.7s ease-out forwards; transform: rotate(180deg) scale(0); opacity: 0; }\n@keyframes spiral { to { transform: rotate(0) scale(1); opacity: 1; } }` },
                    ]
                  },
                  {
                    name: '👆 Effets Survol',
                    styles: [
                      { name: '🔍 Zoom', preview: 'bg-gray-200', css: `.product-card { transition: transform 0.3s ease; }\n.product-card:hover { transform: scale(1.08); z-index: 10; }` },
                      { name: '↗️ Lift', preview: 'bg-gray-200 shadow', css: `.product-card { transition: all 0.3s ease; }\n.product-card:hover { transform: translateY(-10px); box-shadow: 0 20px 40px rgba(0,0,0,0.15); }` },
                      { name: '🎭 Tilt', preview: 'bg-gray-200', css: `.product-card { transition: transform 0.3s ease; transform-style: preserve-3d; }\n.product-card:hover { transform: perspective(1000px) rotateX(5deg) rotateY(-5deg); }` },
                      { name: '💥 Shake', preview: 'bg-red-100', css: `.product-card:hover { animation: shake 0.5s ease-in-out; }\n@keyframes shake { 0%,100% { transform: translateX(0); } 25% { transform: translateX(-5px); } 75% { transform: translateX(5px); } }` },
                      { name: '💓 Heartbeat', preview: 'bg-pink-100', css: `.product-card:hover { animation: heartbeat 0.8s ease-in-out infinite; }\n@keyframes heartbeat { 0%,100% { transform: scale(1); } 50% { transform: scale(1.05); } }` },
                      { name: '🌈 Rainbow', preview: 'bg-gradient-to-r from-red-400 via-yellow-400 to-blue-400', css: `.product-card { transition: all 0.3s ease; }\n.product-card:hover { box-shadow: 0 0 0 3px #ef4444, 0 0 0 6px #fbbf24, 0 0 0 9px #3b82f6; }` },
                    ]
                  },
                  {
                    name: '🎨 Thèmes Complets',
                    styles: [
                      { name: '🌃 Cyberpunk', preview: 'bg-gray-900 border border-cyan-400', css: `.product-card { background: #0f172a; border: 1px solid #22d3ee; border-radius: 0; box-shadow: 0 0 10px rgba(34,211,238,0.3), inset 0 0 10px rgba(34,211,238,0.1); }\n.product-card:hover { box-shadow: 0 0 20px rgba(34,211,238,0.5), inset 0 0 20px rgba(34,211,238,0.2); }\n.shop-btn { background: transparent; border: 2px solid #22d3ee; color: #22d3ee; clip-path: polygon(10% 0, 100% 0, 90% 100%, 0 100%); }\n.shop-btn:hover { background: #22d3ee; color: #0f172a; }` },
                      { name: '🌿 Nature', preview: 'bg-gradient-to-br from-green-100 to-emerald-200', css: `.product-card { background: linear-gradient(135deg, #ecfdf5, #d1fae5); border: 2px solid #a7f3d0; border-radius: 20px; }\n.product-card:hover { transform: translateY(-5px); box-shadow: 0 15px 30px rgba(16,185,129,0.2); }\n.shop-btn { background: linear-gradient(90deg, #10b981, #059669); border-radius: 50px; }\n.shop-btn:hover { box-shadow: 0 0 20px rgba(16,185,129,0.5); }` },
                      { name: '🎀 Kawaii', preview: 'bg-gradient-to-br from-pink-200 to-purple-200', css: `.product-card { background: linear-gradient(145deg, #fce7f3, #f5d0fe); border: 3px solid #f9a8d4; border-radius: 30px; }\n.product-card:hover { transform: scale(1.03) rotate(1deg); }\n.shop-btn { background: linear-gradient(90deg, #ec4899, #a855f7); border-radius: 50px; font-weight: bold; }\n.shop-btn:hover { animation: wiggle 0.5s ease; }\n@keyframes wiggle { 0%,100% { transform: rotate(0); } 25% { transform: rotate(-3deg); } 75% { transform: rotate(3deg); } }` },
                      { name: '🏛️ Luxe', preview: 'bg-gradient-to-br from-amber-100 to-yellow-200 border border-amber-400', css: `.product-card { background: linear-gradient(145deg, #fffbeb, #fef3c7); border: 2px solid #d97706; border-radius: 4px; }\n.product-card:hover { box-shadow: 0 0 30px rgba(217,119,6,0.3); }\n.shop-btn { background: linear-gradient(90deg, #d97706, #b45309); border-radius: 2px; letter-spacing: 2px; text-transform: uppercase; }\n.shop-btn:hover { box-shadow: 0 0 20px rgba(217,119,6,0.5); }` },
                      { name: '🚀 Futuriste', preview: 'bg-slate-900 border border-indigo-500', css: `.product-card { background: linear-gradient(145deg, #1e1b4b, #312e81); border: 1px solid #6366f1; border-radius: 16px; }\n.product-card:hover { border-color: #818cf8; box-shadow: 0 0 40px rgba(99,102,241,0.3); }\n.shop-btn { background: linear-gradient(90deg, #6366f1, #8b5cf6); clip-path: polygon(5% 0, 100% 0, 95% 100%, 0 100%); }\n.shop-btn:hover { filter: brightness(1.2); }` },
                      { name: '🏖️ Summer', preview: 'bg-gradient-to-br from-orange-200 to-cyan-200', css: `.product-card { background: linear-gradient(135deg, #fed7aa, #a5f3fc); border: none; border-radius: 20px; }\n.product-card:hover { transform: translateY(-8px) rotate(2deg); box-shadow: 0 20px 40px rgba(251,146,60,0.2); }\n.shop-btn { background: linear-gradient(90deg, #f97316, #06b6d4); border-radius: 50px; }\n.shop-btn:hover { transform: scale(1.1); }` },
                    ]
                  },
                  {
                    name: '🖼️ Images Produits',
                    styles: [
                      { name: '⚫ N&B → Couleur', preview: 'bg-gray-400', css: `.product-card img { filter: grayscale(100%); transition: filter 0.5s ease; }\n.product-card:hover img { filter: grayscale(0%); }` },
                      { name: '🔆 Brightness', preview: 'bg-yellow-100', css: `.product-card img { filter: brightness(0.9); transition: all 0.3s ease; }\n.product-card:hover img { filter: brightness(1.1); transform: scale(1.05); }` },
                      { name: '🎨 Saturate', preview: 'bg-gradient-to-r from-gray-300 to-red-300', css: `.product-card img { filter: saturate(0.8); transition: filter 0.4s ease; }\n.product-card:hover img { filter: saturate(1.5); }` },
                      { name: '📷 Vintage', preview: 'bg-amber-100', css: `.product-card img { filter: sepia(30%) contrast(1.1); transition: filter 0.4s ease; }\n.product-card:hover img { filter: sepia(0%) contrast(1); }` },
                      { name: '🌙 Dark Overlay', preview: 'bg-gray-800', css: `.product-card img { transition: all 0.3s ease; }\n.product-card::after { content: ''; position: absolute; inset: 0; background: rgba(0,0,0,0.3); transition: opacity 0.3s; }\n.product-card:hover::after { opacity: 0; }` },
                      { name: '🔮 Blur Reveal', preview: 'bg-purple-100', css: `.product-card img { filter: blur(2px); transition: filter 0.4s ease; }\n.product-card:hover img { filter: blur(0); }` },
                    ]
                  }
                ];
                
                return (
                  <div className="mb-6">
                    {/* Onglets catégories */}
                    <div className="flex flex-wrap gap-2 mb-4 pb-3 border-b">
                      {cssCategories.map((cat, idx) => (
                        <button
                          key={idx}
                          type="button"
                          onClick={() => setActiveCssCategory(idx)}
                          className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                            activeCssCategory === idx
                              ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-200'
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                        >
                          {cat.name}
                        </button>
                      ))}
                    </div>
                    
                    {/* Grille de styles */}
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {cssCategories[activeCssCategory].styles.map((style, idx) => (
                        <button
                          key={idx}
                          type="button"
                          onClick={() => setFormData(prev => ({ 
                            ...prev, 
                            custom_css: prev.custom_css 
                              ? prev.custom_css + '\n\n/* ' + style.name + ' */\n' + style.css 
                              : '/* ' + style.name + ' */\n' + style.css 
                          }))}
                          className="group relative p-3 rounded-xl border-2 border-gray-200 hover:border-indigo-400 hover:shadow-md transition-all text-left"
                        >
                          <div className={`h-10 rounded-lg mb-2 ${style.preview} flex items-center justify-center transition-transform group-hover:scale-105`}>
                            <div className="w-5 h-3 bg-current opacity-30 rounded"></div>
                          </div>
                          <span className="text-xs font-medium text-gray-700 group-hover:text-indigo-600">
                            {style.name}
                          </span>
                          <span className="absolute top-1 right-1 text-[10px] bg-indigo-100 text-indigo-600 px-1.5 py-0.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
                            + Ajouter
                          </span>
                        </button>
                      ))}
                    </div>
                    
                    <p className="text-xs text-gray-400 mt-3 text-center">
                      💡 Cliquez pour ajouter le style au CSS. Combinez plusieurs styles !
                    </p>
                  </div>
                );
              })()}

              {/* Textarea CSS */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-gray-500">
                    Ou écrivez votre propre CSS :
                  </p>
                  {formData.custom_css && (
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, custom_css: '' }))}
                      className="text-xs text-red-500 hover:text-red-700"
                    >
                      🗑️ Effacer
                    </button>
                  )}
                </div>
                <textarea
                  name="custom_css"
                  value={formData.custom_css}
                  onChange={handleChange}
                  rows={6}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 font-mono text-sm bg-gray-50"
                  placeholder=".product-card { box-shadow: 0 4px 6px rgba(0,0,0,0.1); }"
                />
              </div>
            </div>
          </>
        )}

        {/* TAB: Livraison */}
        {activeTab === 'delivery' && (
          <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">Options de commande</h2>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between py-2 border-b">
                <div>
                  <div className="font-medium text-gray-800">Livraison à domicile</div>
                  <div className="text-sm text-gray-500">Autoriser les livraisons</div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    name="delivery_enabled"
                    checked={formData.delivery_enabled}
                    onChange={handleChange}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                </label>
              </div>

              <div className="flex items-center justify-between py-2 border-b">
                <div>
                  <div className="font-medium text-gray-800">Retrait sur place</div>
                  <div className="text-sm text-gray-500">Permettre le retrait à la boutique</div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    name="pickup_enabled"
                    checked={formData.pickup_enabled}
                    onChange={handleChange}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                </label>
              </div>

              {formData.delivery_enabled && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Frais de livraison ({formData.currency})
                  </label>
                  <input
                    type="number"
                    name="delivery_fee"
                    value={formData.delivery_fee}
                    onChange={handleChange}
                    min="0"
                    className="w-full md:w-48 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Montant minimum de commande ({formData.currency})
                </label>
                <input
                  type="number"
                  name="min_order_amount"
                  value={formData.min_order_amount}
                  onChange={handleChange}
                  min="0"
                  className="w-full md:w-48 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
                <p className="text-xs text-gray-500 mt-1">0 = pas de minimum</p>
              </div>
            </div>
          </div>
        )}

        {/* TAB: Réseaux sociaux */}
        {activeTab === 'social' && (
          <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">📱 Réseaux sociaux</h2>
            <p className="text-sm text-gray-500 mb-4">
              Ajoutez les liens vers vos réseaux sociaux pour que vos clients puissent vous suivre
            </p>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <span className="inline-flex items-center gap-2">
                    <span className="text-blue-600">📘</span> Facebook
                  </span>
                </label>
                <input
                  type="url"
                  name="social_facebook"
                  value={formData.social_facebook}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="https://facebook.com/votreboutique"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <span className="inline-flex items-center gap-2">
                    <span className="text-pink-600">📷</span> Instagram
                  </span>
                </label>
                <input
                  type="url"
                  name="social_instagram"
                  value={formData.social_instagram}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="https://instagram.com/votreboutique"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <span className="inline-flex items-center gap-2">
                    <span>🎵</span> TikTok
                  </span>
                </label>
                <input
                  type="url"
                  name="social_tiktok"
                  value={formData.social_tiktok}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="https://tiktok.com/@votreboutique"
                />
              </div>
            </div>
          </div>
        )}

      </form>

      {/* Bouton Save sticky en bas */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg p-4 z-40">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div className="text-sm text-gray-500 hidden sm:block">
            {slugAvailable === false ? (
              <span className="text-red-500">⚠️ Le slug est déjà utilisé</span>
            ) : (
              <span>💡 N'oubliez pas d'enregistrer vos modifications</span>
            )}
          </div>
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              document.querySelector('form')?.requestSubmit();
            }}
            disabled={saving || slugAvailable === false}
            className="w-full sm:w-auto px-8 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl hover:from-indigo-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all font-semibold shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
          >
            {saving ? (
              <>
                <span className="animate-spin">⏳</span>
                Enregistrement...
              </>
            ) : (
              <>
                <span>💾</span>
                Enregistrer les modifications
              </>
            )}
          </button>
        </div>
      </div>

      {/* Spacer pour éviter que le contenu soit caché par le bouton sticky */}
      <div className="h-24"></div>

      {/* Toast notification */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      {/* Animation CSS pour le Toast */}
      <style>{`
        @keyframes slide-in {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        .animate-slide-in {
          animation: slide-in 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default BusinessShop;
